import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, getStatusBadge } from '@/backoffice/components/ui';
import { formatCurrency } from '@/backoffice/utils';
import { fetchOrderDetail, payOrder, fetchClientWallet } from '@/domains/client/services/client.service';
import { listPaymentMethods } from '@/domains/api/delivery.api';
import type { Order } from '@/domains/client/services/client.service';
import type { ApiPaymentMethod } from '@/domains/api/types';
import { isCashMethod, isMobileMoneyMethod } from '@/domains/client/utils/paymentMethod';
import { getApiErrorMessage } from '@/utils/apiErrors';
import {
  Smartphone,
  Check,
  Package,
  MapPin,
  Loader2,
  Info,
  AlertCircle,
  Banknote,
  Wallet,
} from 'lucide-react';

const providers = [
  { id: 'orange' as const, label: 'Orange Money', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { id: 'mtn' as const, label: 'MTN MoMo', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
];

export function ConfirmPayPage() {
  const { id } = useParams<{ id: string }>();
  const orderId = Number(id);
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<ApiPaymentMethod[]>([]);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [cashPreferenceSaved, setCashPreferenceSaved] = useState(false);

  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
  const [provider, setProvider] = useState<'orange' | 'mtn'>('orange');
  const [payerPhone, setPayerPhone] = useState('');

  const isPayNowFlow = order?.statut !== 'delivered';
  const paymentTiming = isPayNowFlow ? 'before_delivery' as const : 'after_delivery' as const;

  useEffect(() => {
    if (!orderId) return;
    Promise.all([fetchOrderDetail(orderId), listPaymentMethods(), fetchClientWallet().catch(() => null)])
      .then(([o, methods, wallet]) => {
        setOrder(o);
        setPaymentMethods(methods);
        setWalletBalance(wallet?.balance ?? null);
        if (o.payment_method) {
          const existing = methods.find((m) => m.name === o.payment_method);
          if (existing) setSelectedMethodId(existing.id);
        } else if (methods.length > 0) {
          const preferred = methods.find((m) => isMobileMoneyMethod(m.name)) ?? methods[0];
          setSelectedMethodId(preferred.id);
        }
      })
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  const resolvedMethod = useMemo(() => {
    if (order?.payment_method) {
      return paymentMethods.find((m) => m.name === order.payment_method) ?? null;
    }
    return paymentMethods.find((m) => m.id === selectedMethodId) ?? null;
  }, [order?.payment_method, paymentMethods, selectedMethodId]);

  const isCash = isCashMethod(resolvedMethod?.name);
  const isMobile = isMobileMoneyMethod(resolvedMethod?.name);

  const isPaid = order?.payment_status === 'paid';
  const isConfirmed = order?.statut === 'confirmed_livree';
  const canPayAtDelivery = order?.statut === 'delivered' && !isPaid;
  const canPayNow = order && ['pending', 'in_progress'].includes(order.statut) && !isPaid && !!order.payment_method;
  const canPay = canPayAtDelivery || canPayNow;

  const handlePay = async () => {
    if (!order) return;
    if (isMobile && !payerPhone.trim()) return;

    const methodId = order.payment_method
      ? paymentMethods.find((m) => m.name === order.payment_method)?.id ?? selectedMethodId
      : selectedMethodId;
    if (!methodId) return;

    const amount = order.prix_estime ?? 0;
    if (isMobile && walletBalance != null && walletBalance < amount) {
      setError('Solde portefeuille insuffisant. Rechargez votre portefeuille avant de payer.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload: Parameters<typeof payOrder>[1] = {
        payment_timing: paymentTiming,
        payment_method_id: methodId,
      };
      if (isMobile) {
        payload.payer_phone = payerPhone.trim();
        payload.mobile_money_provider = provider;
      }

      const updated = await payOrder(order.id, payload);
      setOrder(updated);

      if (isMobile && amount > 0) {
        setWalletBalance((prev) => (prev == null ? prev : Math.max(0, prev - amount)));
      }

      if (isCash && paymentTiming === 'before_delivery') {
        setCashPreferenceSaved(true);
      } else {
        setSuccess(true);
      }
    } catch (e) {
      setError(getApiErrorMessage(
        e,
        isCash
          ? 'Impossible d\'enregistrer le paiement en espèces.'
          : 'Échec du paiement. Vérifiez votre solde et réessayez.',
      ));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="client" title="Confirmer & Payer">
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement...
        </div>
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout userType="client" title="Commande introuvable">
        <Card className="text-center py-12">
          <p className="text-gray-500 mb-4">Cette commande n'existe pas.</p>
          <Link to="/dashboard/client/commandes">
            <Button>Retour aux commandes</Button>
          </Link>
        </Card>
      </DashboardLayout>
    );
  }

  if (cashPreferenceSaved) {
    return (
      <DashboardLayout
        userType="client"
        title="Paiement en espèces"
        breadcrumbs={[
          { label: 'Commandes', path: '/dashboard/client/commandes' },
          { label: order.code_suivi, path: `/dashboard/client/commandes/${order.id}` },
          { label: 'Paiement' },
        ]}
      >
        <Card className="text-center py-12 max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <Banknote className="w-8 h-8 text-amber-700" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Espèces à la livraison</h2>
          <p className="text-gray-600 mb-6">
            Vous paierez le livreur en espèces à la réception. Le règlement sera confirmé après livraison.
          </p>
          <Link to={`/dashboard/client/commandes/${order.id}`}>
            <Button>Voir la commande</Button>
          </Link>
        </Card>
      </DashboardLayout>
    );
  }

  if (success || isPaid || isConfirmed) {
    const paidViaCash = isCashMethod(order.payment_method);
    return (
      <DashboardLayout
        userType="client"
        title="Paiement confirmé"
        breadcrumbs={[
          { label: 'Commandes', path: '/dashboard/client/commandes' },
          { label: order.code_suivi, path: `/dashboard/client/commandes/${order.id}` },
          { label: 'Paiement' },
        ]}
      >
        <Card className="text-center py-12 max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Paiement réussi</h2>
          <p className="text-gray-600 mb-2">
            {formatCurrency(order.prix_estime ?? 0)} payé via{' '}
            {paidViaCash ? 'espèces' : 'Mobile Money'}
          </p>
          {order.payment_reference && (
            <p className="text-sm text-gray-500 mb-6">Réf. {order.payment_reference}</p>
          )}
          <div className="flex gap-3 justify-center">
            <Link to={`/dashboard/client/commandes/${order.id}`}>
              <Button variant="outline">Voir la commande</Button>
            </Link>
            {!isConfirmed && (
              <Link to="/dashboard/client/suivi">
                <Button>Suivre la livraison</Button>
              </Link>
            )}
          </div>
        </Card>
      </DashboardLayout>
    );
  }

  if (!canPay) {
    return (
      <DashboardLayout userType="client" title="Paiement indisponible">
        <Card className="text-center py-12 max-w-lg mx-auto">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Paiement non disponible</h2>
          <p className="text-gray-600 mb-6">
            {order.statut !== 'delivered' && !order.payment_method
              ? 'Cette commande sera payable une fois livrée. Utilisez « Confirmer & Payer » depuis le détail de commande.'
              : 'Le paiement n\'est pas disponible pour cette commande dans son état actuel.'}
          </p>
          <Link to={`/dashboard/client/commandes/${order.id}`}>
            <Button>Retour à la commande</Button>
          </Link>
        </Card>
      </DashboardLayout>
    );
  }

  const showMethodPicker = !order.payment_method || canPayAtDelivery;
  const canSubmit = !!resolvedMethod && (!isMobile || !!payerPhone.trim()) && (!showMethodPicker || !!selectedMethodId);

  return (
    <DashboardLayout
      userType="client"
      title="Confirmer & Payer"
      breadcrumbs={[
        { label: 'Commandes', path: '/dashboard/client/commandes' },
        { label: order.code_suivi, path: `/dashboard/client/commandes/${order.id}` },
        { label: 'Paiement' },
      ]}
    >
      <div className="max-w-2xl mx-auto space-y-6">
        <Card className={isCash ? 'bg-amber-50 border-amber-200' : 'bg-teal-50 border-teal-200'}>
          <div className="flex items-start gap-3">
            {isCash
              ? <Banknote className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
              : <Smartphone className="w-6 h-6 text-teal-600 shrink-0 mt-0.5" />}
            <div>
              <h2 className={`font-semibold ${isCash ? 'text-amber-900' : 'text-teal-900'}`}>
                Confirmer & Payer la livraison
              </h2>
              <p className={`text-sm mt-1 ${isCash ? 'text-amber-800' : 'text-teal-800'}`}>
                {isCash
                  ? canPayAtDelivery
                    ? 'Confirmez que vous avez remis les espèces au livreur.'
                    : 'Vous paierez en espèces à la réception du colis.'
                  : canPayAtDelivery
                    ? 'Confirmez la réception : le montant sera débité de votre portefeuille.'
                    : 'Le montant sera débité de votre portefeuille. Rechargez-le si besoin avant de payer.'}
              </p>
            </div>
          </div>
        </Card>

        <Card className="bg-blue-50 border-blue-100">
          <div className="flex items-start gap-2 text-sm text-blue-800">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              Les points de collecte et livraison sur la carte sont positionnés selon la <strong>zone</strong>
              {' '}(Kaloum, Ratoma, etc.), pas l'adresse exacte. Le GPS du livreur est en temps réel.
            </p>
          </div>
        </Card>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Commande</p>
              <p className="font-semibold text-gray-900">{order.code_suivi}</p>
            </div>
            {getStatusBadge(order.statut)}
          </div>

          <div className="space-y-2 text-sm text-gray-600 mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span>{order.departure_zone} → {order.arrival_zone}</span>
            </div>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-400" />
              <span>{order.type_livraison}</span>
            </div>
          </div>

          <div className="flex justify-between items-center py-3 border-t border-gray-100">
            <span className="font-medium text-gray-900">Montant à payer</span>
            <span className="text-2xl font-bold text-teal-600">
              {formatCurrency(order.prix_estime ?? 0)}
            </span>
          </div>
          {isMobile && walletBalance != null && (
            <div className="flex justify-between items-center pt-2 text-sm">
              <span className="text-gray-500 flex items-center gap-1.5">
                <Wallet className="w-4 h-4" />
                Solde portefeuille
              </span>
              <span className={`font-semibold ${
                walletBalance < (order.prix_estime ?? 0) ? 'text-red-600' : 'text-gray-900'
              }`}>
                {formatCurrency(walletBalance)}
              </span>
            </div>
          )}
          {isMobile && walletBalance != null && walletBalance < (order.prix_estime ?? 0) && (
            <p className="text-xs text-red-600 mt-2">
              Solde insuffisant.{' '}
              <Link to="/dashboard/client/portefeuille" className="underline font-medium">
                Recharger mon portefeuille
              </Link>
            </p>
          )}
        </Card>

        <Card>
          {showMethodPicker && (
            <>
              <h3 className="font-semibold text-gray-900 mb-3">Mode de paiement</h3>
              {paymentMethods.length === 0 ? (
                <p className="text-sm text-amber-600 mb-4">Aucun mode de paiement configuré.</p>
              ) : (
                <div className="grid gap-3 mb-6">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setSelectedMethodId(method.id)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedMethodId === method.id
                          ? 'bg-teal-50 border-teal-400'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{method.name}</p>
                          {method.description && (
                            <p className="text-sm text-gray-500">{method.description}</p>
                          )}
                        </div>
                        {selectedMethodId === method.id && (
                          <Check className="w-5 h-5 text-teal-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {!showMethodPicker && order.payment_method && (
            <p className="text-sm text-gray-600 mb-4">
              Mode : <span className="font-medium text-gray-900">{order.payment_method}</span>
            </p>
          )}

          {isMobile && (
            <>
              <h3 className="font-semibold text-gray-900 mb-3">Opérateur Mobile Money</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {providers.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setProvider(p.id)}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      provider === p.id ? `${p.color} border-current` : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <Input
                label="Numéro Mobile Money"
                value={payerPhone}
                onChange={(e) => setPayerPhone(e.target.value)}
                placeholder="Ex: 620 00 00 01"
                hint="Vous recevrez une demande de confirmation sur votre téléphone"
              />
            </>
          )}

          {isCash && (
            <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-4 text-sm text-amber-900">
              <p className="font-medium mb-1">Paiement en espèces</p>
              <p>
                Aucun numéro ni opérateur n’est requis. Préparez le montant exact ;
                le livreur confirmera la remise à la livraison.
              </p>
            </div>
          )}
        </Card>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate(-1)} className="flex-1">
            Retour
          </Button>
          <Button
            className="flex-1"
            onClick={handlePay}
            isLoading={submitting}
            disabled={!canSubmit}
            leftIcon={isCash ? <Banknote className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
          >
            {isCash && paymentTiming === 'before_delivery'
              ? 'Confirmer les espèces'
              : 'Confirmer le paiement'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
